import React from 'react';
import { AiChat } from '@/components/Layout/Menu/HeaderV2/AiChat';
import { merlinActions } from '@/redux/modules';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { AI_AGENTS_MERLIN_SITES_CHAT_ID } from '@/config/GrowthBook/constants';
import { useResize } from '../useResize';
import { AiChatForm } from './components/AiChatForm';
import { useChatbot } from '@/contexts/Chatbot';
import useLocale from '../useLocale';

const useAiChat = () => {
  const dispatch = useDispatch();
  const chatbot = useChatbot();

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width <= 960;

  const { aichat: aichatLocale } = useLocale();

  const chatId = useFeatureValue(AI_AGENTS_MERLIN_SITES_CHAT_ID);

  const {
    renderInHouseChat,
    minimizedChat,
    visibleAiChat,
    currentChat,
    chatHistory: history,
    alerts,
    alertsTypes,
  } = useSelector(state => state.merlin);

  const cookieName = 'merlinUrl';

  const handleInitialInteraction = ({ fullScreen = false, generateThreadId = false } = {}) => {
    dispatch(merlinActions.setChatData({ cookieName, chatId, isIframeOpen: true }));

    if (chatbot.isActive) {
      chatbot.renderChat({ fullscreen: fullScreen && !isMobile });
      return;
    }

    dispatch(merlinActions.inhouse.renderInHouseChat(true));
    dispatch(merlinActions.inhouse.minimizeInHousechat(false));

    if (generateThreadId) {
      dispatch(merlinActions.inhouse.generateThreadId());
    }

    if (fullScreen) {
      dispatch(merlinActions.inhouse.toggleFullScreen());
    }
    setTimeout(() => {
      dispatch(merlinActions.inhouse.visibilityInHousechat(true));
    }, 100);
  };

  const handleOpenAlerts = () => {
    if (chatbot.isActive) {
      chatbot.openAlerts({
        botMessage: aichatLocale.alerts.defaultGatorMessage,
        alerts,
        alertsTypes,
        fullscreen: !isMobile,
      });
      return;
    }
    handleInitialInteraction();
    (!isMobile && (currentChat && !currentChat.fullScreen)) && dispatch(merlinActions.inhouse.toggleFullScreen());

    if (history.length === 0 && !isMobile) {
      const clientMessage = {
        type: 'text',
        text: aichatLocale.alerts.defaultGatorMessage,
        src: 'bot',
      };
      dispatch(merlinActions.inhouse.setMessage(clientMessage));
    }
    dispatch(merlinActions.alerts.showAlerts(true));
  };

  const handleOpenMarketingAgent = () => {
    if (chatbot.isActive) {
      chatbot.renderChat({ fullscreen: !isMobile });
      chatbot.setAgent('marketing');
      return;
    }
    handleInitialInteraction();

    !isMobile && dispatch(merlinActions.inhouse.toggleFullScreen());
    dispatch(merlinActions.inhouse.setMaiaAgent('marketing'));
  };

  const triggerExternalAudioBlob = (blob, fullScreen = true) => {
    if (blob) {
      if (chatbot.isActive) {
        chatbot.renderChat({ fullscreen: fullScreen && !isMobile });
        chatbot.sendAudioBlob(blob);
        return;
      }
      dispatch(merlinActions.inhouse.setExternalAudioBlob(blob));
      const forwardFullScreen = fullScreen && !isMobile;
      handleInitialInteraction({ fullScreen: forwardFullScreen, generateThreadId: true });
    }
  };

  const triggerExternalMessage = ({ message, fullScreen = true, websocketType }) => {
    if (message) {
      const messageTrimmed = message.trim();
      if (chatbot.isActive) {
        chatbot.renderChat({ fullscreen: fullScreen && !isMobile });
        chatbot.sendTextMessage({ message: messageTrimmed, websocketType });
        return;
      }
      dispatch(merlinActions.inhouse.setExternalMessage(messageTrimmed));
      const clientMessage = {
        type: 'text',
        text: messageTrimmed,
        src: 'client',
      };
      dispatch(merlinActions.inhouse.setMessage(clientMessage));
      const forwardFullScreen = fullScreen && !isMobile;
      handleInitialInteraction({ fullScreen: forwardFullScreen, generateThreadId: true });
    }
  };

  const getAiChatProps = () => {
    const handleClose = () => {
      dispatch(merlinActions.inhouse.renderInHouseChat(false));
    };

    const handleToggleMinimize = (bool) => {
      dispatch(merlinActions.inhouse.minimizeInHousechat(bool));
    };

    const setVisibleAiChat = (bool) => {
      dispatch(merlinActions.inhouse.visibilityInHousechat(bool));
    };

    return {
      handleClose,
      minimizedChat,
      handleToggleMinimize,
      visibleAiChat,
      setVisibleAiChat,
      renderInHouseChat,
    };
  };

  const handleAiChatRender = () => (
    !!renderInHouseChat && !chatbot.isActive && (
      <AiChat {...getAiChatProps()} />
    )
  );

  const handleRenderFullScreenAiChat = () => (
    !chatbot.isActive && (
      <AiChat {...getAiChatProps()} />
    )
  );

  const aiChatForm = ({ withoutMargin }) => <AiChatForm withoutMargin={withoutMargin} />;

  return {
    handleAiChatRender,
    handleInitialInteraction,
    handleRenderFullScreenAiChat,
    handleOpenMarketingAgent,
    triggerExternalAudioBlob,
    triggerExternalMessage,
    aiChatForm,
    handleOpenAlerts,
  };
};

export default useAiChat;
